import React, { useDeferredValue, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  FaArrowLeft,
  FaEdit,
  FaGlobeAfrica,
  FaLayerGroup,
  FaLink,
  FaSignal,
  FaUnlink,
  FaUsers,
} from "react-icons/fa";
import adminApi from "services/admin-api";
import { useAuth } from "context/auth-context";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import {
  DetailSectionSkeleton,
  EmptyState,
  PageHeader,
  SectionCard,
  StatCard,
  StatsGridSkeleton,
  StatusPill,
} from "components/admin";
import RightDrawer from "components/global/RightDrawer";
import { DataTable } from "components/molecules/table/table";
import { SportForm } from "components/molecules/forms/sport-form/sport-form";
import { getSportsWorkspaceContent } from "pages/app/sports/sports-access";
import { isRecoverableUploadResponseError } from "utils/admin";
import { hasPortalAction } from "utils/portal-access";
import { getSportVisual } from "utils/sport-icons";
import {
  SportProfileAthleteAvatar,
  SportProfileAthleteCard,
  SportProfileAthleteActionButton,
  SportProfileAthleteFooterRow,
  SportProfileAthleteFooterText,
  SportProfileAthleteGrid,
  SportProfileAthleteHeader,
  SportProfileAthleteIdentity,
  SportProfileAthleteMetaCard,
  SportProfileAthleteMetaGrid,
  SportProfileAthleteMetaLabel,
  SportProfileAthleteMetaValue,
  SportProfilePaginationBar,
  SportProfilePaginationButton,
  SportProfilePaginationControls,
  SportProfilePaginationLabel,
  SportProfilePaginationText,
  SportProfileOverviewDetails,
  SportProfileOverviewIdentity,
  SportProfileOverviewItemIcon,
  SportProfileOverviewLabel,
  SportProfileOverviewLabelGroup,
  SportProfileOverviewLayout,
  SportProfileOverviewRow,
  SportProfileOverviewSportBadge,
  SportProfileOverviewSportChip,
  SportProfileOverviewThumbnailFrame,
  SportProfileOverviewValue,
  SportProfileSecondaryButton,
  SportProfilePrimaryButton,
  SportProfileInfoRow,
  SportProfileSectionTertiary,
  SportProfileGridLayout,
  SportProfileGridLayoutSecondary,
  SportProfileContentStack,
  SportProfileThumbnailImage,
  SportProfilePreviewImage,
  SportProfileSearchInput,
  SportProfileSectionActionButton,
  SportProfileSectionActions,
  SportProfileLabelText,
  SportProfileInlineLinkButton,
  SportProfileLinkForm,
  SportProfileLinkFormActions,
  SportProfileLinkBrowseToggle,
  SportProfileLinkFormCopy,
  SportProfileLinkFormInput,
  SportProfileLinkFormLabel,
  SportProfileLinkFormSelect,
  SportProfileLinkSearchResultButton,
  SportProfileLinkSearchResults,
  SportProfileLinkSearchResultText,
  SportProfileMetaText,
  } from "pages/app/sports/styles/sport-profile.page.inline.styles";
const SportProfilePage: React.FC = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { portalAccess } = useAuth();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isLinkTeamDrawerOpen, setLinkTeamDrawerOpen] = useState(false);
  const [isLinkAthleteDrawerOpen, setLinkAthleteDrawerOpen] = useState(false);
  const [selectedLinkTeamId, setSelectedLinkTeamId] = useState("");
  const [selectedLinkAthleteId, setSelectedLinkAthleteId] = useState("");
  const [selectedLinkAthleteTeamId, setSelectedLinkAthleteTeamId] = useState("");
  const [linkAthletePosition, setLinkAthletePosition] = useState("");
  const [linkAthleteSearchValue, setLinkAthleteSearchValue] = useState("");
  const [showAthleteBrowseSelect, setShowAthleteBrowseSelect] = useState(false);
  const [athleteSearchValue, setAthleteSearchValue] = useState("");
  const [athletePage, setAthletePage] = useState(1);
  const deferredAthleteSearch = useDeferredValue(athleteSearchValue);
  const deferredLinkAthleteSearch = useDeferredValue(linkAthleteSearchValue);
  const athletePageSize = 8;
  const canManageSports = hasPortalAction(portalAccess, "sports.manage");
  const canManageTeams = hasPortalAction(portalAccess, "teams.manage");
  const canManagePlayers = hasPortalAction(portalAccess, "players.manage");
  const workspaceContent = getSportsWorkspaceContent(portalAccess, canManageSports);
  const { data: sport, isLoading } = useQuery({
    queryKey: ["sport", id],
    queryFn: () => adminApi.sports.get(id),
    enabled: Boolean(id),
  });
  const { data: teams = [] } = useQuery({
    queryKey: ["sport-teams", id],
    queryFn: () => adminApi.sports.getTeams(id),
    enabled: Boolean(id) && workspaceContent.showProfileNetworkSections,
  });
  const { data: allTeams = [] } = useQuery({
    queryKey: ["teams"],
    queryFn: () => adminApi.teams.list(),
    enabled: Boolean(id) && workspaceContent.showProfileNetworkSections,
  });
  const { data: allAthletes = [] } = useQuery({
    queryKey: ["athletes"],
    queryFn: () => adminApi.athletes.list(),
    enabled: Boolean(id) && workspaceContent.showProfileNetworkSections,
  });
  const { data: athletes = [], isLoading: isAthletesLoading } = useQuery({
    queryKey: ["sport-athletes", id],
    queryFn: () => adminApi.sports.getAthletes(id),
    enabled: Boolean(id) && workspaceContent.showProfileNetworkSections,
  });
  const filteredAthletes = athletes.filter((athlete: any) =>
    [
      athlete.full_name,
      athlete.position,
      athlete.country,
      athlete.primary_team?.name,
      athlete.status,
    ]
      .join(" ")
      .toLowerCase()
      .includes(deferredAthleteSearch.trim().toLowerCase()),
  );
  const totalAthletePages = Math.max(Math.ceil(filteredAthletes.length / athletePageSize), 1);
  const paginatedAthletes = filteredAthletes.slice(
    (athletePage - 1) * athletePageSize,
    athletePage * athletePageSize,
  );

  useEffect(() => {
    setAthletePage(1);
  }, [deferredAthleteSearch]);

  useEffect(() => {
    if (athletePage > totalAthletePages) {
      setAthletePage(totalAthletePages);
    }
  }, [athletePage, totalAthletePages]);
  const availableTeamsToLink = allTeams.filter(
    (team: any) =>
      !teams.some((linkedTeam: any) => String(linkedTeam.id) === String(team.id)) &&
      team.sport !== sport?.name,
  );
  const availableAthletesToLink = allAthletes.filter(
    (athlete: any) =>
      !athletes.some((linkedAthlete: any) => String(linkedAthlete.id) === String(athlete.id)),
  );
  const filteredAvailableAthletesToLink = availableAthletesToLink.filter((athlete: any) =>
    [
      athlete.full_name,
      athlete.position,
      athlete.country,
      athlete.primary_team?.name,
      ...(athlete.sport_names || []),
    ]
      .join(" ")
      .toLowerCase()
      .includes(deferredLinkAthleteSearch.trim().toLowerCase()),
  );
  const visibleAthleteLinkSuggestions = filteredAvailableAthletesToLink.slice(0, 6);
  const sportTeamIds = teams.map((team: any) => String(team.id));
  const sportVisual = getSportVisual(sport?.name);
  const canEditSport = canManageSports;
  const canLinkTeams = canManageSports && canManageTeams;
  const canLinkAthletes = canManageSports && canManagePlayers;
  const canUnlinkAthletes = canManageSports && canManagePlayers;
  const resetLinkAthleteForm = () => {
    setSelectedLinkAthleteId("");
    setSelectedLinkAthleteTeamId("");
    setLinkAthletePosition("");
    setLinkAthleteSearchValue("");
    setShowAthleteBrowseSelect(false);
  };
  const selectAthleteToLink = (athleteToLink: any) => {
    setSelectedLinkAthleteId(String(athleteToLink.id));
    setLinkAthletePosition(athleteToLink?.position || "");
    setLinkAthleteSearchValue(athleteToLink.full_name || "");
  };
  const updateMutation = useMutation({
    mutationFn: (payload: any) =>
      adminApi.sports.update(id, {
        ...payload,
        founded_year: payload.founded_year ? Number(payload.founded_year) : undefined,
        popularity_rank: payload.popularity_rank ? Number(payload.popularity_rank) : undefined,
      }),
    onSuccess: () => {
      toast.success("Sport updated.");
      queryClient.invalidateQueries({
        queryKey: ["sport", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["sports"],
      });
      setDrawerOpen(false);
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("Sport updated and image refreshed.");
        queryClient.invalidateQueries({
          queryKey: ["sport", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["sports"],
        });
        setDrawerOpen(false);
        return;
      }
      toast.error(error?.response?.data?.error || "Unable to update this sport.");
    },
  });
  const linkTeamMutation = useMutation({
    mutationFn: async () => {
      const teamToLink = allTeams.find((team: any) => String(team.id) === String(selectedLinkTeamId));

      if (!teamToLink || !sport?.name) {
        throw new Error("Choose a team to link.");
      }

      return adminApi.teams.update(String(teamToLink.id), {
        name: teamToLink.name,
        sport: sport.name,
        country: teamToLink.country || "",
        country_id: teamToLink.country_id || null,
        level: teamToLink.level || "",
        player_count: teamToLink.player_count ?? 0,
        image: teamToLink.image_url || teamToLink.image || "",
        status: teamToLink.status || "active",
      });
    },
    onSuccess: () => {
      toast.success("Team linked to sport.");
      queryClient.invalidateQueries({
        queryKey: ["sport-teams", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["sport-athletes", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["teams"],
      });
      queryClient.invalidateQueries({
        queryKey: ["sports"],
      });
      setSelectedLinkTeamId("");
      setLinkTeamDrawerOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || error?.message || "Unable to link this team.");
    },
  });
  const linkAthleteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLinkAthleteId || !selectedLinkAthleteTeamId) {
        throw new Error("Choose an athlete and team first.");
      }

      const athleteToLink: any = await adminApi.athletes.get(String(selectedLinkAthleteId));
      const existingTeamAssignments = Array.isArray(athleteToLink?.team_names_and_positions)
        ? athleteToLink.team_names_and_positions
        : [];
      const existingTeamIds = existingTeamAssignments
        .map((team: any) => String(team.id))
        .filter((teamId: string) => teamId);
      const existingPositionsByTeamId = new Map(
        existingTeamAssignments.map((team: any) => [String(team.id), team.position || ""]),
      );
      const normalizedPosition =
        linkAthletePosition.trim() || athleteToLink?.position?.trim?.() || "";

      if (!normalizedPosition) {
        throw new Error("Add a position for this athlete first.");
      }

      const nextTeamIds = Array.from(
        new Set([...existingTeamIds, String(selectedLinkAthleteTeamId)]),
      );
      const nextPositions = nextTeamIds.map(
        (teamId) => existingPositionsByTeamId.get(String(teamId)) || normalizedPosition,
      );

      return adminApi.athletes.update(String(selectedLinkAthleteId), {
        position: normalizedPosition,
        team_ids: nextTeamIds,
        positions: nextPositions,
      });
    },
    onSuccess: () => {
      toast.success("Athlete linked to sport.");
      queryClient.invalidateQueries({
        queryKey: ["sport-athletes", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["athletes"],
      });
      resetLinkAthleteForm();
      setLinkAthleteDrawerOpen(false);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error || error?.message || "Unable to link this athlete.",
      );
    },
  });
  const unlinkAthleteMutation = useMutation({
    mutationFn: async (athleteToUnlink: any) => {
      const teamAssignments = Array.isArray(athleteToUnlink?.team_names_and_positions)
        ? athleteToUnlink.team_names_and_positions
        : [];
      const remainingAssignments = teamAssignments.filter(
        (teamAssignment: any) => !sportTeamIds.includes(String(teamAssignment.id)),
      );

      return adminApi.athletes.update(String(athleteToUnlink.id), {
        team_ids: remainingAssignments.map((teamAssignment: any) => String(teamAssignment.id)),
        positions: remainingAssignments.map(
          (teamAssignment: any) => teamAssignment.position || athleteToUnlink.position || "",
        ),
      });
    },
    onSuccess: () => {
      toast.success("Athlete unlinked from sport.");
      queryClient.invalidateQueries({
        queryKey: ["sport-athletes", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["athletes"],
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to unlink this athlete.");
    },
  });
  return (
    <AppLayout route="/sports">
      <PageHeader
        title={sport?.name || "Sport profile"}
        description={workspaceContent.profileDescription}
        crumbs={[
          {
            label: "Dashboard",
            to: "/dashboard",
          },
          {
            label: "Sports",
            to: "/sports",
          },
          {
            label: sport?.name || "Sport",
          },
        ]}
        actions={
          <>
            <SportProfileSecondaryButton onClick={() => history.push("/sports")} type="button">
              <FaArrowLeft size={12} />
              Back
            </SportProfileSecondaryButton>
            {canEditSport && workspaceContent.showProfileNetworkSections ? (
              <SportProfilePrimaryButton onClick={() => setDrawerOpen(true)} type="button">
                <FaEdit size={12} />
                Edit sport
              </SportProfilePrimaryButton>
            ) : null}
          </>
        }
      />

      {isLoading ? (
        <StatsGridSkeleton />
      ) : workspaceContent.showProfileNetworkSections ? (
        <SportProfileGridLayout>
          <StatCard label="Teams" value={teams.length || sport?.teams_count || 0} />
          <StatCard label="Athletes" value={athletes.length || sport?.athletes_count || 0} />
          <StatCard label="Founded" value={sport?.founded_year || "Unknown"} />
          <StatCard label="Popularity Rank" value={sport?.popularity_rank || "N/A"} />
        </SportProfileGridLayout>
      ) : null}

      <SportProfileGridLayoutSecondary>
        <SectionCard subtitle={workspaceContent.profileOverviewSubtitle} title="Overview">
          {isLoading ? (
            <DetailSectionSkeleton imageClassName="h-32 w-32 max-w-full sm:h-44 sm:w-44" />
          ) : (
            <SportProfileContentStack>
              <SportProfileOverviewLayout>
                <SportProfileOverviewThumbnailFrame>
                  <SportProfilePreviewImage
                    alt={sport?.name || "Sport"}
                    fallbackLabel={sport?.name}
                    src={sport?.image_url}
                    variant="sport"
                  />
                  <SportProfileOverviewSportBadge
                    $accent={sportVisual.accent}
                    $glow={sportVisual.glow}
                    aria-hidden="true"
                  >
                    <sportVisual.Icon size={18} />
                  </SportProfileOverviewSportBadge>
                </SportProfileOverviewThumbnailFrame>
                <SportProfileOverviewDetails>
                  <SportProfileOverviewIdentity>
                    <SportProfileOverviewSportChip
                      $accent={sportVisual.accent}
                      $glow={sportVisual.glow}
                    >
                      <sportVisual.Icon size={14} />
                      <span>{sport?.name || "Sport"}</span>
                    </SportProfileOverviewSportChip>
                  </SportProfileOverviewIdentity>
                  <SportProfileOverviewRow>
                    <SportProfileOverviewLabelGroup>
                      <SportProfileOverviewItemIcon aria-hidden="true">
                        <FaGlobeAfrica size={12} />
                      </SportProfileOverviewItemIcon>
                      <SportProfileOverviewLabel>Region</SportProfileOverviewLabel>
                    </SportProfileOverviewLabelGroup>
                    <SportProfileOverviewValue>
                      {sport?.region || "Unspecified"}
                    </SportProfileOverviewValue>
                  </SportProfileOverviewRow>
                  <SportProfileOverviewRow>
                    <SportProfileOverviewLabelGroup>
                      <SportProfileOverviewItemIcon aria-hidden="true">
                        <FaLayerGroup size={12} />
                      </SportProfileOverviewItemIcon>
                      <SportProfileOverviewLabel>Level</SportProfileOverviewLabel>
                    </SportProfileOverviewLabelGroup>
                    <SportProfileOverviewValue>
                      {sport?.level || "Unspecified"}
                    </SportProfileOverviewValue>
                  </SportProfileOverviewRow>
                  <SportProfileOverviewRow>
                    <SportProfileOverviewLabelGroup>
                      <SportProfileOverviewItemIcon aria-hidden="true">
                        <FaSignal size={12} />
                      </SportProfileOverviewItemIcon>
                      <SportProfileOverviewLabel>Status</SportProfileOverviewLabel>
                    </SportProfileOverviewLabelGroup>
                    <SportProfileOverviewValue>
                      <StatusPill value={sport?.status} />
                    </SportProfileOverviewValue>
                  </SportProfileOverviewRow>
                  <SportProfileOverviewRow>
                    <SportProfileOverviewLabelGroup>
                      <SportProfileOverviewItemIcon aria-hidden="true">
                        <FaUsers size={12} />
                      </SportProfileOverviewItemIcon>
                      <SportProfileOverviewLabel>Athlete Reach</SportProfileOverviewLabel>
                    </SportProfileOverviewLabelGroup>
                    <SportProfileOverviewValue>
                      {sport?.athletes_count || athletes.length} athletes linked
                    </SportProfileOverviewValue>
                  </SportProfileOverviewRow>
                </SportProfileOverviewDetails>
              </SportProfileOverviewLayout>
            </SportProfileContentStack>
          )}
        </SectionCard>

        {workspaceContent.showProfileNetworkSections ? (
          <SectionCard
            action={
              canLinkTeams ? (
                <SportProfileSectionActionButton
                  disabled={!availableTeamsToLink.length}
                  onClick={() => setLinkTeamDrawerOpen(true)}
                  type="button"
                >
                  <FaLink size={12} />
                  Link team
                </SportProfileSectionActionButton>
              ) : undefined
            }
            subtitle="Teams currently mapped to this sport"
            title="Teams"
          >
            {teams.length ? (
              <DataTable
                canEdit={false}
                canRemove={false}
                columns={[
                  {
                    label: "Team",
                    accessor: (row: any) => (
                      <SportProfileInfoRow>
                        <SportProfileThumbnailImage
                          alt={row.name || "Team"}
                          fallbackLabel={row.name}
                          src={row.image_url}
                          variant="team"
                        />
                        <div>
                          <SportProfileLabelText>{row.name}</SportProfileLabelText>
                          <SportProfileMetaText>{row.country}</SportProfileMetaText>
                        </div>
                      </SportProfileInfoRow>
                    ),
                  },
                  {
                    label: "Level",
                    accessor: (row: any) => row.level || "Not set",
                  },
                  {
                    label: "Status",
                    accessor: (row: any) => <StatusPill value={row.status} />,
                  },
                ]}
                data={teams}
                hideNav
                onView={(teamId) => history.push(`/teams/${teamId}`)}
              />
            ) : (
              <EmptyState
                description="Teams linked to this sport will appear here once they are assigned."
                title="No teams linked yet"
              />
            )}
          </SectionCard>
        ) : null}
      </SportProfileGridLayoutSecondary>

      {workspaceContent.showProfileNetworkSections ? (
        <SportProfileSectionTertiary>
          <SectionCard
            action={
              <SportProfileSectionActions>
                <SportProfileSearchInput
                  onChange={(event) => setAthleteSearchValue(event.target.value)}
                  placeholder="Search athletes"
                  type="search"
                  value={athleteSearchValue}
                />
                {canLinkAthletes ? (
                  <SportProfileSectionActionButton
                    disabled={!teams.length || !availableAthletesToLink.length}
                    onClick={() => setLinkAthleteDrawerOpen(true)}
                    type="button"
                  >
                    <FaLink size={12} />
                    Link athlete
                  </SportProfileSectionActionButton>
                ) : null}
              </SportProfileSectionActions>
            }
            subtitle="Athletes currently competing in this sport"
            title="Athletes"
          >
            {isAthletesLoading ? (
              <DetailSectionSkeleton imageClassName="h-16 w-16 rounded-[20px]" />
            ) : filteredAthletes.length ? (
              <>
                <SportProfileAthleteGrid>
                  {paginatedAthletes.map((athlete: any) => (
                    <SportProfileAthleteCard
                      key={athlete.id}
                      onClick={() => history.push(`/athletes/${athlete.id}`)}
                      type="button"
                    >
                      <SportProfileAthleteHeader>
                        <SportProfileAthleteAvatar
                          alt={athlete.full_name || "Athlete"}
                          fallbackLabel={athlete.full_name}
                          src={athlete.avatar_url || athlete.image}
                          variant="athlete"
                        />
                        <SportProfileAthleteIdentity>
                          <SportProfileLabelText>
                            {athlete.full_name || "Unnamed athlete"}
                          </SportProfileLabelText>
                          <SportProfileMetaText>
                            {athlete.position || "Position pending"}
                          </SportProfileMetaText>
                        </SportProfileAthleteIdentity>
                      </SportProfileAthleteHeader>

                      <SportProfileAthleteMetaGrid>
                        <SportProfileAthleteMetaCard>
                          <SportProfileAthleteMetaLabel>Team</SportProfileAthleteMetaLabel>
                          <SportProfileAthleteMetaValue>
                            {athlete.primary_team?.id ? (
                              <SportProfileInlineLinkButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  history.push(`/teams/${athlete.primary_team.id}`);
                                }}
                                type="button"
                              >
                                {athlete.primary_team.name}
                              </SportProfileInlineLinkButton>
                            ) : (
                              "Unassigned"
                            )}
                          </SportProfileAthleteMetaValue>
                        </SportProfileAthleteMetaCard>
                        <SportProfileAthleteMetaCard>
                          <SportProfileAthleteMetaLabel>Country</SportProfileAthleteMetaLabel>
                          <SportProfileAthleteMetaValue>
                            {athlete.country || "Unknown"}
                          </SportProfileAthleteMetaValue>
                        </SportProfileAthleteMetaCard>
                        <SportProfileAthleteMetaCard>
                          <SportProfileAthleteMetaLabel>Followers</SportProfileAthleteMetaLabel>
                          <SportProfileAthleteMetaValue>
                            {athlete.follower_count ?? 0}
                          </SportProfileAthleteMetaValue>
                        </SportProfileAthleteMetaCard>
                        <SportProfileAthleteMetaCard>
                          <SportProfileAthleteMetaLabel>Status</SportProfileAthleteMetaLabel>
                          <SportProfileAthleteMetaValue>
                            {athlete.status || "Active"}
                          </SportProfileAthleteMetaValue>
                        </SportProfileAthleteMetaCard>
                      </SportProfileAthleteMetaGrid>

                      <SportProfileAthleteFooterRow>
                        <SportProfileAthleteFooterText>
                          Open athlete profile
                        </SportProfileAthleteFooterText>
                        {canUnlinkAthletes ? (
                          <SportProfileAthleteActionButton
                            disabled={
                              unlinkAthleteMutation.isPending &&
                              String((unlinkAthleteMutation.variables as any)?.id) ===
                                String(athlete.id)
                            }
                            onClick={(event) => {
                              event.stopPropagation();

                              if (
                                !window.confirm(
                                  `Unlink ${athlete.full_name || "this athlete"} from ${sport?.name || "this sport"}?`,
                                )
                              ) {
                                return;
                              }

                              unlinkAthleteMutation.mutate(athlete);
                            }}
                            type="button"
                          >
                            <FaUnlink size={10} />
                            {unlinkAthleteMutation.isPending &&
                            String((unlinkAthleteMutation.variables as any)?.id) ===
                              String(athlete.id)
                              ? "Unlinking..."
                              : "Unlink"}
                          </SportProfileAthleteActionButton>
                        ) : null}
                      </SportProfileAthleteFooterRow>
                    </SportProfileAthleteCard>
                  ))}
                </SportProfileAthleteGrid>

                <SportProfilePaginationBar>
                  <SportProfilePaginationText>
                    Showing {(athletePage - 1) * athletePageSize + 1} to{" "}
                    {Math.min(athletePage * athletePageSize, filteredAthletes.length)} of{" "}
                    {filteredAthletes.length} athletes
                  </SportProfilePaginationText>
                  <SportProfilePaginationControls>
                    <SportProfilePaginationButton
                      disabled={athletePage === 1}
                      onClick={() => setAthletePage((currentPage) => currentPage - 1)}
                      type="button"
                    >
                      Previous
                    </SportProfilePaginationButton>
                    <SportProfilePaginationLabel>
                      Page {athletePage} of {totalAthletePages}
                    </SportProfilePaginationLabel>
                    <SportProfilePaginationButton
                      disabled={athletePage >= totalAthletePages}
                      onClick={() => setAthletePage((currentPage) => currentPage + 1)}
                      type="button"
                    >
                      Next
                    </SportProfilePaginationButton>
                  </SportProfilePaginationControls>
                </SportProfilePaginationBar>
              </>
            ) : athletes.length ? (
              <EmptyState
                description="Try a different athlete name, team, country, or position."
                title="No athletes match this search"
              />
            ) : (
              <EmptyState
                description="Athletes linked to this sport will appear here once profiles are associated."
                title="No athletes linked yet"
              />
            )}
          </SectionCard>
        </SportProfileSectionTertiary>
      ) : null}

      <RightDrawer isOpen={isDrawerOpen} onClose={() => setDrawerOpen(false)} title="Edit sport">
        <SportForm
          initialValues={{
            name: sport?.name || "",
            region: sport?.region || "",
            level: sport?.level || "",
            status: sport?.status || "active",
            founded_year: sport?.founded_year || null,
            popularity_rank: sport?.popularity_rank || null,
            image: sport?.image_url || "",
          }}
          onCancel={() => setDrawerOpen(false)}
          onSubmit={(values) => updateMutation.mutate(values)}
        />
      </RightDrawer>

      <RightDrawer
        isOpen={isLinkTeamDrawerOpen}
        onClose={() => {
          setLinkTeamDrawerOpen(false);
          setSelectedLinkTeamId("");
        }}
        title="Link team"
      >
        <SportProfileLinkForm
          onSubmit={(event) => {
            event.preventDefault();

            if (!selectedLinkTeamId) {
              toast.error("Select a team first.");
              return;
            }

            linkTeamMutation.mutate();
          }}
        >
          <SportProfileLinkFormCopy>
            <SportProfileMetaText>
              Link an existing team to {sport?.name || "this sport"}. Any athletes already assigned
              to that team will automatically appear in this sport's Athletes section.
            </SportProfileMetaText>
          </SportProfileLinkFormCopy>

          <div className="space-y-2">
            <SportProfileLinkFormLabel htmlFor="sport-team-link-select">
              Select team
            </SportProfileLinkFormLabel>
            <SportProfileLinkFormSelect
              id="sport-team-link-select"
              onChange={(event) => setSelectedLinkTeamId(event.target.value)}
              value={selectedLinkTeamId}
            >
              <option value="">Choose a team</option>
              {availableTeamsToLink.map((team: any) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                  {team.country ? ` | ${team.country}` : ""}
                  {team.sport ? ` | ${team.sport}` : ""}
                </option>
              ))}
            </SportProfileLinkFormSelect>
          </div>

          <SportProfileLinkFormActions>
            <SportProfileSecondaryButton
              onClick={() => {
                setLinkTeamDrawerOpen(false);
                setSelectedLinkTeamId("");
              }}
              type="button"
            >
              Cancel
            </SportProfileSecondaryButton>
            <SportProfilePrimaryButton disabled={linkTeamMutation.isPending} type="submit">
              {linkTeamMutation.isPending ? "Linking..." : "Link team"}
            </SportProfilePrimaryButton>
          </SportProfileLinkFormActions>
        </SportProfileLinkForm>
      </RightDrawer>

      <RightDrawer
        isOpen={isLinkAthleteDrawerOpen}
        onClose={() => {
          setLinkAthleteDrawerOpen(false);
          resetLinkAthleteForm();
        }}
        title="Link athlete"
      >
        <SportProfileLinkForm
          onSubmit={(event) => {
            event.preventDefault();

            if (!selectedLinkAthleteId || !selectedLinkAthleteTeamId) {
              toast.error("Select both an athlete and team first.");
              return;
            }

            linkAthleteMutation.mutate();
          }}
        >
          <SportProfileLinkFormCopy>
            <SportProfileMetaText>
              Link an athlete to {sport?.name || "this sport"} by assigning them to one of the
              teams already mapped here.
            </SportProfileMetaText>
          </SportProfileLinkFormCopy>

          <div className="space-y-2">
            <SportProfileLinkFormLabel htmlFor="sport-athlete-search-input">
              Choose athlete
            </SportProfileLinkFormLabel>
            <SportProfileLinkFormInput
              id="sport-athlete-search-input"
              onChange={(event) => {
                setLinkAthleteSearchValue(event.target.value);
                setSelectedLinkAthleteId("");
              }}
              placeholder="Search existing athletes"
              type="search"
              value={linkAthleteSearchValue}
            />
            {deferredLinkAthleteSearch.trim() ? (
              visibleAthleteLinkSuggestions.length ? (
                <SportProfileLinkSearchResults>
                  {visibleAthleteLinkSuggestions.map((athlete: any) => (
                    <SportProfileLinkSearchResultButton
                      key={athlete.id}
                      onClick={() => selectAthleteToLink(athlete)}
                      type="button"
                    >
                      <SportProfileLinkSearchResultText>
                        <SportProfileLabelText>
                          {athlete.full_name || "Unnamed athlete"}
                        </SportProfileLabelText>
                        <SportProfileMetaText>
                          {[athlete.position, athlete.primary_team?.name, athlete.country]
                            .filter(Boolean)
                            .join(" | ") || "No athlete details yet"}
                        </SportProfileMetaText>
                      </SportProfileLinkSearchResultText>
                      {String(selectedLinkAthleteId) === String(athlete.id) ? (
                        <SportProfileMetaText>Selected</SportProfileMetaText>
                      ) : null}
                    </SportProfileLinkSearchResultButton>
                  ))}
                </SportProfileLinkSearchResults>
              ) : (
                <SportProfileMetaText>No athletes match that search yet.</SportProfileMetaText>
              )
            ) : null}
            <SportProfileLinkBrowseToggle
              onClick={() => setShowAthleteBrowseSelect((currentValue) => !currentValue)}
              type="button"
            >
              {showAthleteBrowseSelect ? "Hide browse list" : "Browse all athletes"}
            </SportProfileLinkBrowseToggle>
            {showAthleteBrowseSelect ? (
              <SportProfileLinkFormSelect
                id="sport-athlete-link-select"
                onChange={(event) => {
                  const nextAthleteId = event.target.value;
                  const athleteToLink = availableAthletesToLink.find(
                    (athlete: any) => String(athlete.id) === String(nextAthleteId),
                  );

                  setSelectedLinkAthleteId(nextAthleteId);

                  if (athleteToLink) {
                    selectAthleteToLink(athleteToLink);
                  }
                }}
                value={selectedLinkAthleteId}
              >
                <option value="">Choose an athlete</option>
                {availableAthletesToLink.map((athlete: any) => (
                  <option key={athlete.id} value={athlete.id}>
                    {athlete.full_name || "Unnamed athlete"}
                    {athlete.position ? ` | ${athlete.position}` : ""}
                    {athlete.primary_team?.name ? ` | ${athlete.primary_team.name}` : ""}
                  </option>
                ))}
              </SportProfileLinkFormSelect>
            ) : null}
          </div>

          <div className="space-y-2">
            <SportProfileLinkFormLabel htmlFor="sport-athlete-position-input">
              Position
            </SportProfileLinkFormLabel>
            <SportProfileLinkFormInput
              id="sport-athlete-position-input"
              onChange={(event) => setLinkAthletePosition(event.target.value)}
              placeholder="Forward"
              type="text"
              value={linkAthletePosition}
            />
          </div>

          <div className="space-y-2">
            <SportProfileLinkFormLabel htmlFor="sport-athlete-team-link-select">
              Assign to team
            </SportProfileLinkFormLabel>
            <SportProfileLinkFormSelect
              id="sport-athlete-team-link-select"
              onChange={(event) => setSelectedLinkAthleteTeamId(event.target.value)}
              value={selectedLinkAthleteTeamId}
            >
              <option value="">Choose a team</option>
              {teams.map((team: any) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                  {team.country ? ` | ${team.country}` : ""}
                </option>
              ))}
            </SportProfileLinkFormSelect>
          </div>

          <SportProfileLinkFormActions>
            <SportProfileSecondaryButton
              onClick={() => {
                setLinkAthleteDrawerOpen(false);
                resetLinkAthleteForm();
              }}
              type="button"
            >
              Cancel
            </SportProfileSecondaryButton>
            <SportProfilePrimaryButton disabled={linkAthleteMutation.isPending} type="submit">
              {linkAthleteMutation.isPending ? "Linking..." : "Link athlete"}
            </SportProfilePrimaryButton>
          </SportProfileLinkFormActions>
        </SportProfileLinkForm>
      </RightDrawer>
    </AppLayout>
  );
};
export default SportProfilePage;
